// Taşeron portalı — çok-firma girişinin 2. adımı (api-contract §5.5). Transit token
// + firma listesi login'den router state ile gelir (store'a yazılmaz). Kullanıcı
// firmayı seçer → `/auth/select-workspace` → terminal token → oturum kurulur → panel.
// State yoksa (yenileme / doğrudan gelme) transit süresi dolmuş kabul edilir → login.

import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
  type SupplierCompanyItem,
  resolveApiErrorMessage,
  selectWorkspace,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { Building2, Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface SelectWorkspaceState {
  transitToken?: string;
  companies?: SupplierCompanyItem[];
}

export function SelectWorkspacePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as SelectWorkspaceState | null) ?? {};
  const transitToken = state.transitToken;
  const companies = state.companies ?? [];

  const [selectedId, setSelectedId] = useState<number | null>(companies[0]?.id ?? null);

  const mutation = useMutation({
    mutationFn: (companyId: number) => {
      if (!transitToken) {
        throw new Error('missing-transit');
      }
      return selectWorkspace(companyId, transitToken, companies);
    },
    onSuccess: () => navigate('/', { replace: true }),
  });

  // Transit token yoksa akış geçersiz — kullanıcıyı yeniden girişe yönlendir.
  if (!transitToken || companies.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle as="h1">{t('selectWorkspace.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Alert tone="danger">{t('selectWorkspace.expired')}</Alert>
          <Button asChild variant="secondary" className="w-full">
            <Link to="/login">{t('selectWorkspace.backToLogin')}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h1">{t('selectWorkspace.title')}</CardTitle>
        <CardDescription className="carbon:mb-6">{t('selectWorkspace.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (selectedId != null) {
              mutation.mutate(selectedId);
            }
          }}
        >
          {mutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          <fieldset
            className="flex flex-col gap-2"
            aria-label={t('selectWorkspace.title')}
            disabled={mutation.isPending}
          >
            {companies.map((company) => {
              const active = company.id === selectedId;
              return (
                <label
                  key={company.id}
                  className={[
                    'flex cursor-pointer items-center gap-3 border px-3 py-3 text-sm transition-colors',
                    'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-ring',
                    active
                      ? 'border-primary bg-primary/5 text-fg'
                      : 'border-border-strong text-fg hover:bg-surface-muted',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="company"
                    className="sr-only"
                    value={company.id}
                    checked={active}
                    onChange={() => setSelectedId(company.id)}
                  />
                  <Building2 className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
                  <span className="flex-1 font-medium">{company.name || `#${company.id}`}</span>
                  {active ? (
                    <Check className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  ) : null}
                </label>
              );
            })}
          </fieldset>

          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={selectedId == null}
            className="mt-1 w-full"
          >
            {mutation.isPending ? (
              <Spinner label={t('selectWorkspace.continue')} />
            ) : (
              t('selectWorkspace.continue')
            )}
          </Button>

          <div className="text-center text-sm">
            <Link to="/login" className="text-primary hover:underline">
              {t('selectWorkspace.backToLogin')}
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
